// templateSections.js
// Grouping for the New Game "Ready-Made" picker (#72). Extracted from NewGame.js
// so the section logic is testable and stays honest against the LIVE storyTemplates
// array (which grows at runtime when server-delivered premium campaigns register,
// see registerPremiumTemplates / premiumContentApi.js).
//
// Historically only tier-1 starters were listed, which made tier-2/3 campaigns
// INVISIBLE on New Game until a save completed a campaign and the continue-legend
// picker offered them (a premium subscriber could not even see that The Drowned
// Bells existed). Maintainer directive 2026-07-06: "we don't open up t2 quest
// cards on the game page, but we probably should. Then t3 as appropriate."
//
// Rules:
// - Tier 1 keeps the historical starter split: free starters + premium starters
//   (locked-card teaser for free users).
// - Tier 2 ("Seasoned Parties") and tier 3+ ("Legendary Campaigns") get their own
//   sections. comingSoon stubs are EXCLUDED there: those sections list only
//   playable campaigns, so the tier-3 section simply does not render until real
//   t3 content exists (local dev slot or server delivery).
// - Premium templates are SEGREGATED within every tier, mirroring the tier-1
//   free/premium split (maintainer 2026-07-06: "The sand and snow ones should
//   be segregated as premium"): each tier section renders its free grid first,
//   then a premium sub-section with the gold treatment. Card-level gating
//   (canUseTemplate) still applies inside the premium groups.

import { storyTemplates } from '../data/storyTemplates';
import { isTemplatePremium } from './entitlements';

/**
 * Canonical tier -> level bands, the single source of truth for every generic
 * "Tier N (Lv X-Y)" label and requirement gate (maintainer 2026-07-06: the
 * custom builder and the ready-made sections had drifted apart). Individual
 * templates still carry their own levelRange for their cards; these bands
 * describe the TIER as a concept: derived from the authored catalog census
 * (t1 all 1-2; t2 3-5 with a couple of 3-4s inside the band; t3 4-7, entered
 * at 5 for a standard party).
 */
export const TIER_LEVEL_BANDS = Object.freeze({
    1: Object.freeze([1, 2]),
    2: Object.freeze([3, 5]),
    3: Object.freeze([5, 7]),
});

/** "Lv 3-5" style label for a tier. */
export const tierBandLabel = (tier) => {
    const band = TIER_LEVEL_BANDS[tier];
    return band ? `Lv ${band[0]}-${band[1]}` : '';
};


/**
 * Split a template catalog into the New Game picker's sections.
 * @param {Array<object>} [templates] - defaults to the live storyTemplates array
 * @returns {{ freeStarters: Array, premiumStarters: Array, seasoned: Array, premiumSeasoned: Array, legendary: Array, premiumLegendary: Array }}
 */
export const getTemplateSections = (templates = storyTemplates) => {
    const starters = templates.filter((t) => t.tier === 1);
    const seasoned = templates.filter((t) => t.tier === 2 && !t.comingSoon);
    const legendary = templates.filter((t) => (t.tier || 1) >= 3 && !t.comingSoon);
    return {
        freeStarters: starters.filter((t) => !isTemplatePremium(t)),
        premiumStarters: starters.filter((t) => isTemplatePremium(t)),
        seasoned: seasoned.filter((t) => !isTemplatePremium(t)),
        premiumSeasoned: seasoned.filter((t) => isTemplatePremium(t)),
        legendary: legendary.filter((t) => !isTemplatePremium(t)),
        premiumLegendary: legendary.filter((t) => isTemplatePremium(t)),
    };
};
